import { useMemo } from 'react'

import { BigNumber } from 'ethers'

import {
  useWeb3RequestQuery,
} from '../features/api/web3ApiSlice'
import { Currency } from '../entities'
import IERC20ABI from '../abi/IERC20.abi.json'
import { HookProvider } from './types'

interface FetchInfo {
  address: string
  currency: Currency | string
}

interface FetchBalanceProps {
  provider?: HookProvider
  fetchInfo: FetchInfo | Array<FetchInfo>
  pollingInterval?: number
}

interface FetchBalanceAction {
  data?: BigNumber | Array<BigNumber | undefined>
  isSuccess: boolean
  isError: boolean
  isFetching: boolean
  isLoading: boolean
}

const DefaultPollingInterval = 10_000

const isCurrency = (
  currencyOrAddress: Currency | string,
): currencyOrAddress is Currency => (<Currency>currencyOrAddress).address !== undefined

const prepareRequest = ({ address, currency }: FetchInfo) => ((isCurrency(currency) && currency.isNative) ? {
  method: 'getBalance',
  params: [address, 'latest'],
} : {
  address: isCurrency(currency) ? currency.address : currency,
  abi: IERC20ABI,
  method: 'balanceOf',
  params: [address],
})

const useFetchBalancePerCurrencies = ({
  provider, fetchInfo, pollingInterval = DefaultPollingInterval,
}: FetchBalanceProps): FetchBalanceAction => {
  const request = useMemo(() => (Array.isArray(fetchInfo)
    ? fetchInfo.map((info) => prepareRequest(info))
    : prepareRequest(fetchInfo)), [fetchInfo])

  const { data, ...queryData } = useWeb3RequestQuery({
    provider,
    request,
  }, {
    pollingInterval,
    skip: !provider,
  })

  const response = useMemo(() => {
    if (!data) return undefined

    return !Array.isArray(data) ? BigNumber.from(data) : data.map((value) => (
      value && BigNumber.from(value)
    )) as Array<BigNumber | undefined>
  }, [data])

  return {
    data: response,
    ...queryData,
  }
}

export default useFetchBalancePerCurrencies
